import React, { useState } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/TextMessageAnalysis.css";
import "../../css/Common.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faExclamationTriangle,
  faTimes,
  faPaperPlane,
  faChartBar,
  faEdit,
  faCommentDots,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import { formatDateTime } from "../../utils/DateTimeUtil";
import HelpGuideButton from "../../HelpGuideButton";
import LinkAnalysisSection from "../common/LinkAnalysisSection";
import ThreatBannerShareButton from "../common/ThreatBannerShareButton";
import EmptyStatePanel from "../common/EmptyStatePanel";
import {
  getRiskLevelIcon,
  getThreatBannerClass,
  getThreatTitle,
  getThreatSubtitle,
  formatScamType,
  getOverallRiskLevel,
  getOverallRiskType,
  getOverallRecommendation,
  decodeAndSanitizeHtml,
  isHighRisk,
  isMediumRisk,
} from "./textAnalysisUtils";

// ─────────────────────────────────────────────
// Sub-Components for Results Display
// ─────────────────────────────────────────────

const ThreatBanner = ({ riskLevel, riskType, reportId }) => (
  <div className={`threat-banner ${getThreatBannerClass(riskLevel)}`}>
    <div className="threat-icon">
      <FontAwesomeIcon icon={getRiskLevelIcon(riskLevel)} />
    </div>
    <div className="threat-content">
      <h2 className="threat-title">{getThreatTitle(riskLevel)}</h2>
      <p className="threat-subtitle">
        {getThreatSubtitle(riskLevel, riskType)}
      </p>
    </div>
    <ThreatBannerShareButton
      scamDetected={isHighRisk(riskLevel) || isMediumRisk(riskLevel)}
      reportId={reportId}
      threatType="Text Message Scam"
      threatTitle={getThreatTitle(riskLevel)}
      threatDetail={getThreatSubtitle(riskLevel, riskType)}
    />
  </div>
);

const ScamTypeDisplay = ({ scamType }) => {
  const { t } = useTranslation();
  if (!scamType) return null;
  const normalized = scamType.toLowerCase().replace(/[_\s/]+/g, "");
  if (
    normalized === "unknown" ||
    normalized === "otheruncategorized" ||
    normalized === "other"
  )
    return null;
  return (
    <div
      className="scam-type-display"
      style={{ marginTop: "var(--spacing-md, 12px)" }}
    >
      <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
      {t("textAnalysis.results.scamType")}{" "}
      <span className="scam-type-badge">{formatScamType(scamType)}</span>
    </div>
  );
};

const RecommendationsSection = ({ recommendation }) => {
  const { t } = useTranslation();
  if (!recommendation) return null;
  return (
    <div className="suggestions-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon icon={faInfoCircle} />{" "}
        {t("textAnalysis.results.whatToDo")}
      </h4>
      <SuggestionsBulletList text={recommendation} />
    </div>
  );
};

const ExplanationSection = ({ textAnalysis, collapsed, onToggle }) => {
  const { t } = useTranslation();
  const hasContent =
    textAnalysis?.explanation ||
    textAnalysis?.reasoning ||
    textAnalysis?.description;
  if (!hasContent) return null;

  return (
    <div className="explanation-section">
      <h4
        className="subsection-title collapsible-header"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon icon={faInfoCircle} />{" "}
        {t("textAnalysis.results.explanation")}
        <FontAwesomeIcon
          icon={collapsed ? faChevronDown : faChevronUp}
          style={{ marginLeft: "auto", fontSize: "0.85rem" }}
        />
      </h4>
      {!collapsed && (
        <div className="explanation-text">
          {textAnalysis.explanation ? (
            <div
              dangerouslySetInnerHTML={{
                __html: decodeAndSanitizeHtml(textAnalysis.explanation),
              }}
            />
          ) : textAnalysis.reasoning ? (
            <div
              dangerouslySetInnerHTML={{
                __html: decodeAndSanitizeHtml(textAnalysis.reasoning),
              }}
            />
          ) : (
            <p>{textAnalysis.description}</p>
          )}
        </div>
      )}
    </div>
  );
};

const PhoneAnalysisSection = ({ phoneAnalysis, expanded, onToggle }) => {
  const { t } = useTranslation();
  if (!phoneAnalysis) return null;

  return (
    <div
      className="explanation-section"
      style={{ marginTop: "var(--spacing-md, 12px)" }}
    >
      <h4
        className="subsection-title collapsible-header"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon icon={faPhone} /> {t("textAnalysis.phone.title")}
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          style={{ marginLeft: "auto", fontSize: "0.85rem" }}
        />
      </h4>
      {expanded && (
        <div className="explanation-text">
          {phoneAnalysis.riskLevel && (
            <p>
              <strong>{t("textAnalysis.phone.riskLevel")}:</strong>{" "}
              {phoneAnalysis.riskLevel}
            </p>
          )}
          {phoneAnalysis.summary && (
            <div>
              <strong>{t("textAnalysis.phone.summary")}:</strong>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(phoneAnalysis.summary),
                }}
              />
            </div>
          )}
          {phoneAnalysis.description && (
            <div>
              <strong>{t("textAnalysis.phone.analysis")}:</strong>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(phoneAnalysis.description),
                }}
              />
            </div>
          )}
          {phoneAnalysis.recommendations?.length > 0 && (
            <div>
              <strong>{t("textAnalysis.phone.whatToDo")}</strong>
              <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem" }}>
                {phoneAnalysis.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
          {phoneAnalysis.sources?.length > 0 && (
            <div style={{ marginTop: "var(--spacing-md, 12px)" }}>
              <strong>
                {t("textAnalysis.phone.dbResults").replace(
                  "{count}",
                  phoneAnalysis.sources.length,
                )}
              </strong>
              {phoneAnalysis.sources.map((source, index) => (
                <div
                  key={index}
                  style={{
                    marginTop: "0.5rem",
                    paddingLeft: "1rem",
                    borderLeft: "2px solid var(--border-color, #e5e7eb)",
                  }}
                >
                  <p style={{ fontWeight: 600 }}>{source.name}</p>
                  <p>
                    {t("textAnalysis.phone.status")}:{" "}
                    {source.found
                      ? t("textAnalysis.phone.foundInDb")
                      : t("textAnalysis.phone.notFound")}
                  </p>
                  {source.reports != null && (
                    <p>
                      {t("textAnalysis.phone.reports")}: {source.reports}
                    </p>
                  )}
                  {source.category && (
                    <p>
                      {t("textAnalysis.phone.category")}: {source.category}
                    </p>
                  )}
                  {source.lastReported && (
                    <p>
                      {t("textAnalysis.phone.lastReported")}:{" "}
                      {source.lastReported}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

const TextMessageAnalysisDesktop = ({
  phoneNumber,
  textMessage,
  userContext,
  setUserContext,
  analysisResults,
  error,
  isLoading,
  hasUserInput,
  phoneValidation,
  reportId,
  handlePhoneChange,
  handleTextChange,
  handleClearAnalysis,
  handleSubmit,
  isFormValid,
  shouldShowPhoneValidationError,
  phoneInputKey,
}) => {
  const [isDatabaseResultsExpanded, setIsDatabaseResultsExpanded] =
    useState(false);
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <div className="common-header">
        <h1 className="main-title">{t("textAnalysis.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">{t("textAnalysis.page.subtitle")}</p>
          <HelpGuideButton guideId={12} />
        </div>
      </div>

      <div className="common-layout">
        <div className="analysis-form-container" style={{ flex: 1 }}>
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faEdit} />
              {t("textAnalysis.form.heading")}
            </h3>
          </div>
          <div className="panel-content">
            {error && (
              <div className="error-message">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="analysis-form">
              {/* Sender Information Row */}
              <div className="sender-info-section">
                <div className="sender-inputs-row">
                  {/* Phone Number Input */}
                  <div className="input-column">
                    <div
                      className={`input-header compact ${
                        isLoading ? "disabled" : ""
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="section-icon"
                      />
                      <label htmlFor="phone-number">
                        {t("textAnalysis.form.phoneLabel")}
                      </label>
                    </div>
                    <div className="common-form-field">
                      <InternationalPhoneInput
                        key={phoneInputKey}
                        id="phone-number"
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        placeholder={t("textAnalysis.form.phonePlaceholder")}
                        disabled={isLoading}
                        className="phone-input compact"
                      />
                      {shouldShowPhoneValidationError() && (
                        <div className="validation-error">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          {t("textAnalysis.form.invalidPhone")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Message Input */}
              <div className="text-input-container">
                <div className="input-header compact">
                  <FontAwesomeIcon
                    icon={faCommentDots}
                    className="section-icon"
                  />
                  <label>{t("textAnalysis.form.messageLabel")}</label>
                </div>
                <textarea
                  value={textMessage}
                  onChange={handleTextChange}
                  placeholder={t("textAnalysis.form.messagePlaceholder")}
                  rows={3}
                  disabled={isLoading}
                  className="text-message-input compact"
                  maxLength={1000}
                />
                <div className="character-count">
                  {t("textAnalysis.form.charCount").replace(
                    "{count}",
                    textMessage.length,
                  )}
                </div>
              </div>

              {/* Context (optional) */}
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="text-context-input">
                  <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
                  {t("textAnalysis.form.contextLabel")}
                </label>
                <textarea
                  id="text-context-input"
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={t("textAnalysis.form.contextPlaceholder")}
                  className="additional-context-textarea"
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <div className="btn-group">
                <button
                  type="submit"
                  disabled={isLoading || !isFormValid()}
                  className="btn btn-primary"
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isLoading
                    ? t("textAnalysis.form.analyzing")
                    : t("textAnalysis.form.analyzeBtn")}
                </button>

                {hasUserInput && (
                  <button
                    type="button"
                    onClick={handleClearAnalysis}
                    disabled={isLoading}
                    className="btn btn-ghost"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    {t("textAnalysis.form.clearBtn")}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Results Display */}
        <div className="analysis-details" style={{ flex: 1 }}>
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faChartBar} />
              {t("textAnalysis.results.heading")}
            </h3>
          </div>
          <div className="panel-content">
            {analysisResults ? (
              <>
                <ThreatBanner
                  riskLevel={getOverallRiskLevel(analysisResults)}
                  riskType={getOverallRiskType(analysisResults)}
                  reportId={reportId}
                />

                <ScamTypeDisplay
                  scamType={analysisResults.textAnalysis?.scamType}
                />

                <RecommendationsSection
                  recommendation={getOverallRecommendation(analysisResults)}
                />

                <ExplanationSection
                  textAnalysis={analysisResults.textAnalysis}
                  collapsed={explanationCollapsed}
                  onToggle={() =>
                    setExplanationCollapsed(!explanationCollapsed)
                  }
                />

                <PhoneAnalysisSection
                  phoneAnalysis={analysisResults.phoneAnalysis}
                  expanded={isDatabaseResultsExpanded}
                  onToggle={() =>
                    setIsDatabaseResultsExpanded(!isDatabaseResultsExpanded)
                  }
                />

                <LinkAnalysisSection
                  urlAnalyses={analysisResults.urlAnalyses}
                />

                {/* Analysis Metadata */}
                {analysisResults.analysisDate && (
                  <div className="analysis-metadata">
                    <small>
                      {t("textAnalysis.results.completedOn")}{" "}
                      {formatDateTime(analysisResults.analysisDate)}
                    </small>
                  </div>
                )}
              </>
            ) : (
              <EmptyStatePanel
                icon={faCommentDots}
                title={t("textAnalysis.results.noAnalysis")}
                description={t("textAnalysis.results.noAnalysisDesc")}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TextMessageAnalysisDesktop;
